import flatten from 'lodash/flatten';
import has from 'lodash/has';

import referrerParser from './referer-parser';
import type { ChannelTypes } from './const';
import type { DetectorParams, GetChannelOptions } from './types';
import detectors from './detectors';

/**
 * Return the first URL
 */
const getFirstUrl = (cookie: Record<string, string>, referrer?: string): string | undefined => {
  const value = has(cookie, 'firstUrl') ? cookie.firstUrl : referrer;
  return value ? decodeURIComponent(value) : undefined;
};

/**
 * Return the first referer
 */
const getFirstReferer = (cookie: Record<string, string>, referrer?: string): string | undefined => {
  const value = has(cookie, 'firstReferer') ? cookie.firstReferer : referrer;
  return value ? decodeURIComponent(value) : undefined;
};

/**
 * Return the URL object
 */
const getURL = (url: string | undefined): URL | undefined => {
  if (!url) {
    return undefined;
  }
  try {
    return new URL(url);
  } catch (e) {
    return undefined;
  }
};

/**
 * Return the detector params
 */
export const getChannelDetectorParams = ({ cookie, query, referrer }: GetChannelOptions): DetectorParams => {
  const firstUrl = getFirstUrl(cookie, referrer);
  const firstReferer = getFirstReferer(cookie, referrer);
  const firstURL = getURL(firstUrl);

  const utmSource = flatten([firstURL?.searchParams?.get?.('utm_source') || query?.['utm_source']])[0];
  let referrerMedium: string | undefined;
  try {
    referrerMedium = firstReferer ? new referrerParser(firstReferer, firstUrl)?.medium : undefined;
  } catch (err) {
    referrerMedium = undefined;
  }
  const detectorParams: DetectorParams = {
    firstUrl: firstURL,
    utmSource,
    firstReferer,
    referrerMedium,
  };

  return detectorParams;
};

/**
 * Detect the channel
 * https://github.com/getjerry/jerry-data-pipeline/blob/main/pipeline/magic/gen_landed_v2.py#L122
 */
export const getChannel = (options: GetChannelOptions): ChannelTypes | undefined => {
  const detectorParams: DetectorParams = getChannelDetectorParams(options);

  for (const director of detectors) {
    if (director?.fn?.(detectorParams)) {
      return director.channel;
    }
  }
};

/**
 * Return true if the channel is the given channel
 */
export const checkChannel = (options: GetChannelOptions, channel: ChannelTypes): boolean => {
  return getChannel(options) === channel;
};
